import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LogmergeApiService } from '../../services/logmerge-api.service';
import { SessionLoggerService } from '../../services/session-logger.service';
import { ProcessingStateService } from '../../services/processing-state.service';

interface LogEntry {
  timestamp: string;
  level: string;
  process: string;
  file: string;
  message: string;
}

@Component({
  selector: 'app-logs',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './logs.component.html',
  styleUrls: ['./logs.component.css']
})
export class LogsComponent implements OnInit {
  logs: LogEntry[] = [];
  filteredLogs: LogEntry[] = [];
  isLoading = false;
  errorMessage = '';
  selectedLevel = 'ALL';
  /**
   * Compact view (default) asks the backend to fold per-file INFO chatter
   * into per-stage summaries. Verbose fetches the raw log stream and is
   * only useful when debugging a specific file.
   */
  compact = true;

  levels = ['ALL', 'INFO', 'WARNING', 'ERROR', 'CRITICAL', 'DEBUG'];

  constructor(
    private apiService: LogmergeApiService,
    private sessionLogger: SessionLoggerService,
    private processingState: ProcessingStateService
  ) {}

  ngOnInit(): void {
    this.loadLogs();
  }

  setCompact(value: boolean): void {
    if (this.compact === value) return;
    this.compact = value;
    this.loadLogs();
  }

  loadLogs(): void {
    this.isLoading = true;
    this.errorMessage = '';

    // Get session logs first
    const sessionLogs = this.sessionLogger.getLogs();

    // Only fetch backend logs if a processing job exists in the current
    // browser session. On a hard refresh the ProcessingStateService is
    // reinstantiated (jobId becomes null), so previous runs' backend logs
    // are not shown until the user starts a new job.
    if (!this.processingState.jobId) {
      this.logs = sessionLogs.map(log => ({
        timestamp: log.timestamp,
        level: log.level,
        process: log.process,
        file: log.file,
        message: log.message
      }));
      this.filterLogs();
      this.isLoading = false;
      return;
    }

    this.apiService.getLogs(this.processingState.jobId, this.compact).subscribe({
      next: (response) => {
        const backendLogs = this.parseLogs(response.logs || []);
        // Combine session logs with backend logs
        this.logs = this.combineLogs(sessionLogs, backendLogs);
        this.filterLogs();
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.errorMessage = 'Backend not available. Showing session logs only.';
        // Show only session logs when backend is unavailable
        this.logs = sessionLogs.map(log => ({
          timestamp: log.timestamp,
          level: log.level,
          process: log.process,
          file: log.file,
          message: log.message
        }));
        this.filterLogs();
      }
    });
  }

  private combineLogs(sessionLogs: any[], backendLogs: LogEntry[]): LogEntry[] {
    // Get session start time for filtering
    const sessionStartTime = this.sessionLogger.getSessionStartTime();

    // Convert session logs to LogEntry format
    const sessionEntries: LogEntry[] = sessionLogs.map(log => ({
      timestamp: log.timestamp,
      level: log.level,
      process: log.process,
      file: log.file,
      message: log.message
    }));

    // // Filter backend logs to only include logs from current session
    // const currentSessionBackendLogs = backendLogs.filter(log => {
    //   try {
    //     const logTime = new Date(log.timestamp);
    //     return logTime >= sessionStartTime;
    //   } catch {
    //     // If timestamp parsing fails, include the log
    //     return true;
    //   }
    // });

    // Combine and sort by timestamp (newest first)
    const combined = [...sessionEntries, ...backendLogs];
    return combined.sort((a, b) => {
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });
  }

  filterLogs(): void {
    if (this.selectedLevel === 'ALL') {
      this.filteredLogs = [...this.logs];
    } else {
      this.filteredLogs = this.logs.filter(log => log.level.toUpperCase() === this.selectedLevel);
    }
  }

  setFilter(level: string): void {
    this.selectedLevel = level;
    this.filterLogs();
  }

  getLevelClass(level: string): string {
    switch (level) {
      case 'ERROR':
      case 'CRITICAL':
        return 'level-error';
      case 'WARNING':
        return 'level-warning';
      case 'INFO':
        return 'level-info';
      case 'DEBUG':
        return 'level-debug';
      default:
        return '';
    }
  }

  private parseLogs(lines: string[]): LogEntry[] {
    return lines
      .filter(line => line.trim() && !line.startsWith('Date'))
      .map(line => {
        const parts = line.split(',').map(p => p.trim().replace(/"/g, ''));
        // CSV format: timestamp, line_number, level, process, file, message
        const hasLineNum = parts.length >= 6 && !isNaN(Number(parts[1]));
        const level = (hasLineNum ? parts[2] : parts[1] || 'INFO').toUpperCase();
        const process = hasLineNum ? parts[3] : parts[2] || '';
        const message = hasLineNum ? parts.slice(5).join(', ') : parts.slice(4).join(', ');

        return {
          timestamp: parts[0] || '',
          level,
          process,
          file: hasLineNum ? parts[4] : parts[3] || '',
          message: this.simplifyMessage(message, process, level)
        };
      })
      .filter(entry => entry.timestamp);
  }

  private simplifyMessage(message: string, process: string, level: string): string {
    const text = (message || '').trim();
    if (!text) return 'No details available.';

    const normalized = text.toLowerCase();

    if (normalized.includes('initialized logmerge')) {
      return 'Log merge started.';
    }
    if (normalized.includes('creating temporary files')) {
      return 'Preparing temporary files.';
    }
    if (normalized.includes('mapping units')) {
      return 'Converting units.';
    }
    if (normalized.includes('unit conversion completed')) {
      return 'Unit conversion completed.';
    }
    if (normalized.includes('mnemonic family conversion completed')) {
      return 'Mnemonic family mapping completed.';
    }
    if (normalized.includes('mnemonic mapping process completed')) {
      return 'Mnemonic mapping completed.';
    }
    if (normalized.includes('resampling completed successfully')) {
      return 'Resampling completed successfully.';
    }
    if (normalized.includes('cannot find uwi value in the header')) {
      return 'Missing UWI in LAS header.';
    }
    if (normalized.startsWith('processing ') && normalized.endsWith('...')) {
      const fileName = text.replace(/^Processing\s+/i, '').replace(/\.\.\.$/, '').trim();
      return fileName ? `Processing file ${fileName}.` : 'Processing file.';
    }

    if (level === 'ERROR' || level === 'CRITICAL') {
      return `Issue: ${text}`;
    }
    if (process) {
      return text;
    }
    return text;
  }
}
